import { html, repeat, when, ref } from '@microsoft/fast-element';
import type { RfqForm } from './rfq-form';
import { sync } from '@genesislcap/foundation-utils';
import { minusSignIcon } from '../../../../styles';
import { roundNumberOptionalField } from '../../../../utils/formatting';
import { getEnumArrayWithoutNotApplicableValue } from '../../../../utils/form';
import { getAbbreviationForCreditProfile } from '../../../../utils/credit-profiles';
import { CONTRACT_TYPE, VPPA_RFQ_STATUS } from '../../../../utils';

export const RfqFormTemplate = html<RfqForm>`
<form>
  <div
    class="form-control"
    ?hidden=${(x) =>
      (x.rfqStatusValue != VPPA_RFQ_STATUS.ACTIVE &&
        x.rfqStatusValue != VPPA_RFQ_STATUS.DELISTED) ||
      !x.isEditModal}
  >
    <label>Status</label>
    <zero-select
      ${ref('rfqStatusElement')}
      :value=${sync((x) => x.rfqStatusValue)}
      class="input-control"
    >
      ${repeat(
        (x) => x.rfqStatuses,
        html`
          <zero-option
            ?hidden="${(x) =>
              x.ENUM_VALUE != VPPA_RFQ_STATUS.ACTIVE && x.ENUM_VALUE != VPPA_RFQ_STATUS.DELISTED}"
            value="${(x) => x.ENUM_VALUE}"
          >
            ${(x) => x.READABLE_VALUE}
          </zero-option>
        `,
      )}
    </zero-select>
  </div>
  <div class="form-control required">
      <label>Product Type</label>
          <zero-select
              ${ref('productTypeElement')}
              :value=${sync((x) => x.productTypeValue)}
              class="input-control"
              ?disabled=${(x) => x.isEditModal}
          >
          <zero-option value="">Select Product Type</zero-option>
              ${repeat(
                (x) => x.productTypes,
                html`
                  <zero-option value="${(x) => x.ENUM_VALUE}">
                    ${(x) => x.READABLE_VALUE}
                  </zero-option>
                `,
              )}
          </zero-select>
      </div>
  <div class="form-control required">
    <label class="form-label">Technology</label>
    <zero-segmented-control
      appearance="primary"
      orientation="horizontal"
      :value="${sync((x) => x.technologyTypeValue)}"
      ${ref('technologyTypeElement')}
    >
      <zero-segmented-item
        @click=${(x) => x.technologyTypeChanged(x.technologyTypeWind)}
        value="${(x) => x.technologyTypeWind}"
        :current-checked=${(x) =>
          x.technologyTypeValue !== null && x.technologyTypeValue === x.technologyTypeWind}
      >
        Wind
      </zero-segmented-item>
      <zero-segmented-item
        @click=${(x) => x.technologyTypeChanged(x.technologyTypeBoth)}
        value="${(x) => x.technologyTypeBoth}"
        style="display: ${(x) => (x.contractTypeValue === x.autoExecString ? 'none' : '')}"
        :current-checked=${(x) =>
          x.technologyTypeValue !== null && x.technologyTypeValue === x.technologyTypeBoth}
      >
        Both
      </zero-segmented-item>
      <zero-segmented-item
        @click=${(x) => x.technologyTypeChanged(x.technologyTypeSolar)}
        value="${(x) => x.technologyTypeSolar}"
        :current-checked=${(x) =>
          x.technologyTypeValue !== null && x.technologyTypeValue === x.technologyTypeSolar}
      >
        Solar
      </zero-segmented-item>
    </zero-segmented-control>
  </div>

    <div class="form-control required">
        <label>Contract Type</label>
        <zero-select 
            ${ref('contractTypeElement')}
            :value=${sync((x) => x.contractTypeValue)}
            class="input-control"
            ?disabled=${(x) =>
              x.isEditModal || x.contractTypeValue === CONTRACT_TYPE.REC || !x.productTypeValue}

        >
        <zero-option value="">Select Contract Type</zero-option>
        ${repeat(
          (x, c) => x.filteredContractTypes(),
          html`
            <zero-option
              ?hidden="${(x, c) =>
                c.parent.technologyTypeValue === c.parent.technologyTypeBoth &&
                x.ENUM_VALUE === c.parent.autoExecString}"
              value="${(x) => x.ENUM_VALUE}"
            >
              ${(x) => x.READABLE_VALUE}
            </zero-option>
          `,
        )}
        </zero-select>
    </div>
    
    <div class="form-control required">
        <label>ISO</label>
        <zero-select 
            ${ref('isoElement')}
            class="input-control"
            :value=${sync((x) => x.isoValue)}
            @change="${(x) => x.isoSelected()}"
            ?disabled=${(x) => x.isEditModal && x.contractTypeValue === x.autoExecString}
        >
        <zero-option value="">Select ISO</zero-option>
        ${repeat(
          (x) => x._iso,
          html`
            <zero-option value="${(x) => x}">${(x) => x}</zero-option>
          `,
        )}
        </zero-select>
    </div>
    <zero-number-field 
        ${ref('requestedVolumeElement')}
        class="input-control"
        placeholder="Enter..." 
        min="0"
        hide-step
        withFormatting
        required
        style="display: ${(x) => (x.contractTypeValue === x.autoExecString ? 'none' : '')}"
        :value=${sync((x) => x.requestedVolumeValue)}
    >Requested Volume (MWh/year)</zero-number-field>

  <zero-number-field
    class="form-control required"
    style="display: ${(x) => (x.contractTypeValue === x.autoExecString ? '' : 'none')}"
      ${ref('capacityElement')}
      placeholder="Enter..."
      :value=${sync((x) => x.capacityValue)}
      hide-step
      required
    >
      Capacity (MW)
  </zero-number-field>
        
  <div
    class="form-control required"
    style="display: ${(x) => (x.contractTypeValue === x.recString ? 'none' : '')}"
  >
    <label>Settlement Point</label>
    <zero-select
      ${ref('settlementPointElement')}
      ?disabled=${(x) => x._settlementPoint.length === 0}
      class="input-control"
      :value=${sync((x) => x.settlementPointValue)}
      @change="${(x) => x.settlementPointSelected()}"
    >
    <zero-option value="">Select Settlement Point</zero-option>
    ${repeat(
      (x) => x._settlementPoint,
      html`
        <zero-option value="${(x) => x}">${(x) => x}</zero-option>
      `,
    )}
    </zero-select>
  </div>

  <div
    class="form-control required"
    style="display: ${(x) => (x.contractTypeValue === x.recString ? 'none' : '')}"
  >
    <label>Settlement Point Index</label>
    <zero-select
      ${ref('settlementIndexElement')}
      ?disabled=${(x) => x._settlementIndex.length === 0}
      :value=${sync((x) => x.settlementIndexValue)}
      class="input-control"
      @change="${(x) => x.settlementIndexSelected()}"
    >
    <zero-option value="">Select Settlement Point Index</zero-option>
    ${repeat(
      (x) => x._settlementIndex,
      html`
        <zero-option value="${(x) => x}">${(x) => x}</zero-option>
      `,
    )}
    </zero-select>
  </div>

  <div
    class="form-control no-padding-bottom required"
    style="display: ${(x) =>
      x.contractTypeValue === x.autoExecString || x.contractTypeValue === x.recString
        ? 'none'
        : ''}"
  >
    <label class="form-label">Price Range <info-icon id="priceRange"></info-icon></label>
    <zero-tooltip anchor="priceRange" position="top">
      The midpoint value of this Fixed Price Range will be displayed as the bid price in Market Depth. The full Fixed Price Range is available to Sellers.
    </zero-tooltip>
    <div class="range-value">
      <zero-text-field
        ${ref('fixedPriceLowerBoundElement')}
        class="input-control"
        type="number"
        placeholder="Price From..."
        min="0"
        :value=${sync((x) => x.fixedPriceLowerBoundValue)}
        oninput="this.value = !!this.value && this.value >= 0 ? this.value : null"
      >
      </zero-text-field>
      <div class="minus-sign">${minusSignIcon('')}</div>
      <zero-text-field
        ${ref('fixedPriceUpperBoundElement')}
        class="input-control"
        type="number"
        min="0"
        placeholder="Price To..."
        :value=${sync((x) => x.fixedPriceUpperBoundValue)}
        oninput="this.value = !!this.value && this.value >= 0 ? this.value : null"
      >
      </zero-text-field>
    </div>
  </div>
          
  <zero-number-field
    class="form-control"
    style="display: ${(x) =>
      x.contractTypeValue === x.autoExecString || x.contractTypeValue === x.recString
        ? ''
        : 'none'}"
    ${ref('fixedPriceElement')}
    type="number"
    placeholder="Enter..."
    :value=${sync((x) => x.fixedPriceValue)}
    hide-step
    required
  >
    Fixed Price
  </zero-number-field>

  <zero-number-field
    class="form-control"
    style="display: ${(x) => (x.contractTypeValue === x.recString ? '' : 'none')}"
    ${ref('floorQuantityElement')}
    type="number"
    placeholder="Enter..."
    :value=${sync((x) => x.floorQuantityValue)}
    hide-step
  >
   Floor Quantity
  </zero-number-field>

  <div
    class="form-control required date long-error"
  >
    <label>Tenor Start Date</label>
    <zero-text-field
      placeholder="Enter..."
      ${ref('tenorStartDateElement')}
      type="date"
      onkeydown="return false"
      :value=${sync((x) => x.tenorStartDateValue)}
    >
    </zero-text-field>
  </div>

    <zero-number-field
            ${ref('tenorMonthElement')}
            class="input-control"
            placeholder="Enter..."
            min="1"
            max="240"
            hide-step
            required
            @blur=${(x) =>
              (x.tenorMonthValue = parseInt(x.tenorMonthValue)
                ? parseInt(x.tenorMonthValue).toString()
                : '')}
            :value=${sync((x) => x.tenorMonthValue)}
    >Tenor (Months)<info-icon id="tenorMonthTooltip"></info-icon></zero-number-field>
    <zero-tooltip anchor="tenorMonthTooltip" position="top">
        Tenor months is a whole number in range from 1 to 240
    </zero-tooltip>

  <div
    class="form-control"
    style="display: ${(x) => (x.contractTypeValue === x.autoExecString ? 'none' : '')}"
  >
    <label>RFQ Reference <info-icon id="rfqReferenceTooltip"></info-icon></label>
      <zero-tooltip anchor="rfqReferenceTooltip" position="top">
          Add an internal note specific to this RFQ. RFQ Reference is not visible to counterparties.
      </zero-tooltip>
    <zero-text-area
      ${ref('rfqReferenceElement')}
      :value=${sync((x) => x.rfqReferenceValue)}
      class="input-control"
      type="text"
      placeholder="Enter..."
      maxlength="64"
    >
    </zero-text-area>
  </div>
  ${when(
    (x) => x.displayCod,
    html<RfqForm>`
      <div
        class="form-control date"
        style="display: ${(x) => (x.contractTypeValue === x.autoExecString ? 'none' : '')}"
      >
        <label>Target COD</label>
        <zero-text-field
          ${ref('targetCodElement')}
          class="input-control"
          type="date"
          onkeydown="return false"
          placeholder="Enter..."
          :value=${sync((x) => x.targetCodValue)}
          @input=${(x, c) => x.validateInputForDateElements(c.event)}
        ></zero-text-field>
      </div>

      <div
        class="form-control date"
        style="display: ${(x) => (x.contractTypeValue === x.autoExecString ? 'none' : '')}"
      >
        <label>Guaranteed COD</label>
        <zero-text-field
          placeholder="Enter..."
          ${ref('guaranteedCodElement')}
          type="date"
          onkeydown="return false"
          :value=${sync((x) => x.guaranteedCodValue)}
        ></zero-text-field>
      </div>
    `,
  )}
        
  <div
    class="form-control"
    style="display: ${(x) => (x.contractTypeValue === x.autoExecString ? 'none' : '')}"
  >
    <label>Country</label>
    <zero-select
      ${ref('countryElement')}
      :value=${sync((x) => x.countryValue)}
      class="input-control"
      disabled="true"
    >
      <zero-option value="">Select Country</zero-option>
      ${repeat(
        (x) => x._allCountries,
        html`
          <zero-option value="${(x) => x.COUNTRY_NAME}">${(x) => x.COUNTRY_NAME}</zero-option>
        `,
      )}
    </zero-select>
  </div>
    
  ${when(
    (e) => e.countryValue === 'United States',
    html<RfqForm>`
      <div
        class="form-control"
        style="display: ${(x) => (x.contractTypeValue === x.autoExecString ? 'none' : '')}"
      >
        <label>State</label>
        <zero-select
          :value=${sync((x) => x.stateValue)}
          class="input-control"
          ?disabled="${(x) => !(x.countryElement.value === 'United States')}"
          ${ref('stateElement')}
          @change="${(x) => x.stateSelected()}"
        >
          <zero-option value="">Select State</zero-option>
          ${repeat(
            (x) => x._allStates,
            html`
              <zero-option value="${(x) => x.STATE_NAME}">${(x) => x.STATE_NAME}</zero-option>
            `,
          )}
        </zero-select>
      </div>

      <div
        class="form-control"
        style="display: ${(x) => (x.contractTypeValue === x.autoExecString ? 'none' : '')}"
      >
        <label>City</label>
        <zero-select
          ${ref('cityElement')}
          :value=${sync((x) => x.cityValue)}
          class="input-control"
          ?disabled="${(x) => !x.stateElement.value}"
        >
          <zero-option value="">Select City</zero-option>
          ${repeat(
            (x) => x._cities,
            html`
              <zero-option value="${(x) => x.CITY_NAME}">${(x) => x.CITY_NAME}</zero-option>
            `,
          )}
        </zero-select>
      </div>
    `,
  )}

  <div
    class="form-control"
    style="display: ${(x) =>
      x.contractTypeValue === x.autoExecString || x.contractTypeValue === x.recString
        ? 'none'
        : ''}"
  >
    <label>Negative Pricing Method</label>
    <zero-select
      ${ref('negativePriceMethodElement')}
      class="input-control"
      :value=${sync((x) => x.negativePriceMethodValue)}
    >
    <zero-option value="">Select Negative Pricing Method</zero-option>
    ${repeat(
      (x) => getEnumArrayWithoutNotApplicableValue(x.negativePricingMethod),
      html`
        <zero-option value="${(x) => x.ENUM_VALUE}">${(x) => x.READABLE_VALUE}</zero-option>
      `,
    )}
    </zero-select>
  </div>

  <div
    class="form-control"
    style="display: ${(x) => (x.contractTypeValue === x.autoExecString ? 'none' : '')}"
  >
    <label>Performance Guarantee Method</label>
      <zero-select
        ${ref('performanceGuaranteeMethodElement')}
        class="input-control"
        :value=${sync((x) => x.performanceGuaranteeMethodValue)}
      >
      <zero-option value="">Select Performance Guarantee Method</zero-option>
      ${repeat(
        (x) => getEnumArrayWithoutNotApplicableValue(x.performanceGuaranteeMethod),
        html`
          <zero-option value="${(x) => x.ENUM_VALUE}">${(x) => x.READABLE_VALUE}</zero-option>
        `,
      )}
      </zero-select>
  </div>
  <div
    class="form-control required"
    style="display: ${(x) => (x.contractTypeValue === x.autoExecString ? 'none' : '')}"
  >
    <label>Applicable REC Program</label>
    <zero-select 
      ${ref('applicableRECProgramElement')}
      class="input-control"
      :value=${sync((x) => x.applicableRECProgramValue)}
      @change="${(x) => x.applicableRecProgramSelected()}"
    >
    <zero-option value="">Select Applicable REC Program</zero-option>

    ${repeat(
      (x) => x.applicableRECProgram,
      html`
        <zero-option value="${(x) => x.ENUM_VALUE}">${(x) => x.READABLE_VALUE}</zero-option>
      `,
    )}
    </zero-select>
  </div>

    <div 
            class="form-control ${(x) => (x.isApplicableRecProgramSelected() ? 'required' : '')}"
            style="display: ${(x) => (x.contractTypeValue === x.autoExecString ? 'none' : '')}"
    >
        <label>Applicable Tracking System</label>
        <zero-select
                ${ref('applicableTrackingSystemElement')}
                class="input-control"
                :value=${sync((x) => x.applicableTrackingSystemValue)}
                ?disabled=${(x) => !x.isApplicableRecProgramSelected()}
        >
            <zero-option value="">Select Applicable Tracking System</zero-option>
            ${repeat(
              (x) => getEnumArrayWithoutNotApplicableValue(x.applicableTrackingSystem),
              html`
                <zero-option value="${(x) => x.ENUM_VALUE}">${(x) => x.READABLE_VALUE}</zero-option>
              `,
            )}
        </zero-select>
    </div>

  <div
    class="form-control ${(x) => (x.isApplicableRecProgramSelected() ? 'required' : '')}"
    style="display: ${(x) => (x.contractTypeValue === x.autoExecString ? 'none' : '')}"
  >
    <label>REC Delivery Methodology</label>
    <zero-select
      ${ref('recDeliveryMethodologyElement')}
      class="input-control"
      :value=${sync((x) => x.recDeliveryMethodologyValue)}
      ?disabled=${(x) => !x.isApplicableRecProgramSelected()}
    >
    <zero-option value="">Select REC Delivery Methodology</zero-option>
    ${repeat(
      (x) => getEnumArrayWithoutNotApplicableValue(x.recDeliveryMethodology),
      html`
        <zero-option value="${(x) => x.ENUM_VALUE}">${(x) => x.READABLE_VALUE}</zero-option>
      `,
    )}
    </zero-select>
  </div>

  <div
    class="form-control required"
    style="display: ${(x) => (x.contractTypeValue === x.autoExecString ? 'none' : '')}"
   >
    <label>Credit Profile</label>
    <zero-select
      ${ref('rfqCreditProfileElement')}
      :value=${sync((x) => x.creditProfileValue)}
      class="input-control"
      @change="${(x) => x.creditProfileSelected()}"
    >
      <zero-option value="">Select Credit Profile</zero-option>
      ${repeat(
        (x) => x._filteredCreditProfile,
        html`
          <zero-option value="${(x) => x.ENUM_VALUE}">${(x) => x.READABLE_VALUE}</zero-option>
        `,
      )}
    </zero-select>
  </div>

  <div
    class="form-control required"
    style="display: ${(x) => (x.contractTypeValue === x.autoExecString ? 'none' : '')}"
  >
  <label>Buyer Post-COD Credit Support (Max)</label>
      <zero-number-field
          ${ref('buyerPostCodCollateralElement')}
          class="input-control"
          type="number"
          withFormatting
          min="0"
          placeholder="${(x) => getAbbreviationForCreditProfile(x.creditProfileValue)}"
          :disabled=${(x) => !x.isCreditWorthy}
          hide-step
          :value=${sync((x) => x.buyerPostCodCollateralValue)}
      >
      </zero-number-field>
  </div>

  <div
    class="key-metrics-section"
    style="display: ${(x) => (x.contractTypeValue === x.autoExecString ? 'none' : 'block')}"
  >
    Key Metrics
  </div>

  <!-- Expected Settlement Range ($/MWh) -->
  <div class="form-control no-padding-bottom" style="display: ${(x) =>
    x.contractTypeValue === x.autoExecString || x.contractTypeValue === x.recString ? 'none' : ''}">
    <label class="form-label">Expected Settlement Range ($/MWh)</label>
    <div class="range-value">
      <zero-number-field
        class="input-control"
        type="text"
        placeholder="Enter Min..."
        hide-step
        @blur=${(x) =>
          (x.expectedSettlementMinValue = roundNumberOptionalField(
            x.expectedSettlementMinValue,
            2,
          ))}
        :value=${sync((x) => x.expectedSettlementMinValue)}
        ${ref('expectedSettlementMinElement')}
      >
      </zero-number-field>
      <div class="minus-sign">${minusSignIcon('')}</div>
      <zero-number-field
        class="input-control"
        type="number"
        placeholder="Enter Max..."
        hide-step
        :value=${sync((x) => x.expectedSettlementMaxValue)}
        @blur=${(x) =>
          (x.expectedSettlementMaxValue = roundNumberOptionalField(
            x.expectedSettlementMaxValue,
            2,
          ))}
        ${ref('expectedSettlementMaxElement')}
      >
      </zero-number-field>
    </div>
  </div>

  <!-- Downside Risk Range ($/MWh) -->
  <div
    class="form-control no-padding-bottom"
    style="display: ${(x) =>
      x.contractTypeValue === x.autoExecString || x.contractTypeValue === x.recString
        ? 'none'
        : ''}"
  >
    <label class="form-label">Downside Risk Range ($/MWh)</label>
    <div class="range-value">
      <zero-number-field
        class="input-control"
        type="number"
        placeholder="Enter Min..."
        :value=${sync((x) => x.downsideRiskMinValue)}
        hide-step
        ${ref('downsideRiskMinElement')}
        @blur=${(x) =>
          (x.downsideRiskMinValue = roundNumberOptionalField(x.downsideRiskMinValue, 2))}
      >
      </zero-number-field>
      <div class="minus-sign">${minusSignIcon('')}</div>
      <zero-number-field
        class="input-control"
        type="number"
        placeholder="Enter Max..."
        :value=${sync((x) => x.downsideRiskMaxValue)}
        ${ref('downsideRiskMaxElement')}
        hide-step
        @blur=${(x) =>
          (x.downsideRiskMaxValue = roundNumberOptionalField(x.downsideRiskMaxValue, 2))}
      >
      </zero-number-field>
    </div>
  </div>

  <!-- Historical Nodal LME Range (MT CO2/MWh) -->
  <div
    class="form-control no-padding-bottom"
    style="display: ${(x) => (x.contractTypeValue === x.autoExecString ? 'none' : '')}"
  >
    <label class="form-label">Historical Nodal LME Range (MT CO2/MWh)</label>
    <div class="range-value">
      <zero-number-field
        class="input-control"
        type="number"
        placeholder="Enter Min..."
        hide-step
        :value=${sync((x) => x.nodalLmeHistoricMinValue)}
        ${ref('nodalLmeHistoricMinElement')}
        @blur=${(x) =>
          (x.nodalLmeHistoricMinValue = roundNumberOptionalField(x.nodalLmeHistoricMinValue, 2))}
      >
      </zero-number-field>
      <div class="minus-sign">${minusSignIcon('')}</div>
      <zero-number-field
        class="input-control"
        type="number"
        placeholder="Enter Max..."
        :value=${sync((x) => x.nodalLmeHistoricMaxValue)}
        hide-step
        ${ref('nodalLmeHistoricMaxElement')}
        @blur=${(x) =>
          (x.nodalLmeHistoricMaxValue = roundNumberOptionalField(x.nodalLmeHistoricMaxValue, 2))}
      >
      </zero-number-field>
    </div>
  </div>

  <!-- Implied Carbon Price Range ($/MT CO2) -->
  <div
    class="form-control no-padding-bottom"
    style="display: ${(x) => (x.contractTypeValue === x.autoExecString ? 'none' : '')}"
  >
    <label class="form-label">Implied Carbon Price Range ($/MT CO2)</label>
      <div class="range-value">
        <zero-number-field
          class="input-control"
          type="number"
          placeholder="Enter Min..."
          :value=${sync((x) => x.impliedCarbonPriceMinValue)}
          hide-step
          ${ref('impliedCarbonPriceMinElement')}
          @blur=${(x) =>
            (x.impliedCarbonPriceMinValue = roundNumberOptionalField(
              x.impliedCarbonPriceMinValue,
              2,
            ))}
        >
        </zero-number-field>
        <div class="minus-sign">${minusSignIcon('')}</div>
        <zero-number-field
          class="input-control"
          type="number"
          placeholder="Enter Max..."
          :value=${sync((x) => x.impliedCarbonPriceMaxValue)}
          hide-step
          ${ref('impliedCarbonPriceMaxElement')}
          @blur=${(x) =>
            (x.impliedCarbonPriceMaxValue = roundNumberOptionalField(
              x.impliedCarbonPriceMaxValue,
              2,
            ))}
        >
        </zero-number-field>
    </div>
  </div>

  <!-- Carbon Free Energy Range (%) -->
  <div
    class="form-control no-padding-bottom"
    style="display: ${(x) =>
      x.contractTypeValue === x.autoExecString || x.contractTypeValue === x.recString
        ? 'none'
        : ''}"
  >
    <label class="form-label">Carbon Free Energy Range (%)</label>
      <div class="range-value">
        <zero-number-field
          class="input-control"
          type="number"
          placeholder="Enter Lower Bound..."
          :value=${sync((x) => x.carbonFreeEnergyLowerBoundValue)}
          hide-step
          ${ref('carbonFreeEnergyLowerBoundElement')}
          @blur=${(x) =>
            (x.carbonFreeEnergyLowerBoundValue = roundNumberOptionalField(
              x.carbonFreeEnergyLowerBoundValue,
              2,
            ))}
        >
        </zero-number-field>
        <div class="minus-sign">${minusSignIcon('')}</div>
        <zero-number-field
          class="input-control"
          type="number"
          placeholder="Enter Upper Bound..."
          :value=${sync((x) => x.carbonFreeEnergyUpperBoundValue)}
          hide-step
          ${ref('carbonFreeEnergyUpperBoundElement')}
          @blur=${(x) =>
            (x.carbonFreeEnergyUpperBoundValue = roundNumberOptionalField(
              x.carbonFreeEnergyUpperBoundValue,
              2,
            ))}
        >
        </zero-number-field>
      </div>
    </div>
  </div>

  <div class="auto-exec-message-div">
    <span
      class="auto-exec-message"
      style="display: ${(x) =>
        x.contractTypeValue !== x.autoExecString || x.isEditModal ? 'none' : ''}"
    >
      By clicking Add New Contract, a bid will be created for a standardized Mini-Post Build VPPA. A transaction will be automatically executed when the bid matches with an offer based on the criteria specified above.
    </span>
  </div>
</form>
`;
